
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface EmailLog {
  id: string;  
  email_id: string;
  to_email: string;
  to_name?: string;
  from_name?: string;
  subject: string;
  entity_type: string;
  entity_id: string;
  status: string;
  error?: string;
  created_at: string;
  last_updated_at?: string;
  metadata?: Record<string, any>;
}

interface EmailLogsFilter {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  entity_type?: string;
  entity_id?: string;
}

export const useEmailLogs = (filter?: EmailLogsFilter) => {
  const fetchEmailLogs = async (): Promise<EmailLog[]> => {
    console.log("Fetching email logs with filter:", filter);
    
    let query = supabase
      .from("email_logs")
      .select("*")
      .order("sent_at", { ascending: false });
    
    // Apply filters if provided
    if (filter) {
      if (filter.status && filter.status !== 'all') {
        query = query.eq('status', filter.status);
      }
      
      if (filter.dateFrom) {
        query = query.gte('sent_at', filter.dateFrom);
      }
      
      if (filter.dateTo) {
        query = query.lte('sent_at', filter.dateTo);
      }
      
      if (filter.search) {
        query = query.or(`to_email.ilike.%${filter.search}%,subject.ilike.%${filter.search}%`);
      }

      if (filter.entity_type) {
        query = query.eq('entity_type', filter.entity_type);
      }

      if (filter.entity_id) {
        query = query.eq('entity_id', filter.entity_id);
      }
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error fetching email logs:", error);
      throw new Error(`Failed to fetch email logs: ${error.message}`);
    }
    
    // Transform the data to match our EmailLog interface
    const emailLogs = (data || []).map(log => ({
      id: log.id,
      email_id: log.email_id,
      to_email: log.to_email,
      to_name: log.to_name,
      from_name: log.from_name,
      subject: log.subject,
      entity_type: log.entity_type || "",
      entity_id: log.entity_id || "",
      status: log.status,
      error: log.error,
      // Use sent_at for created_at since that field doesn't exist in the database
      created_at: log.sent_at || new Date().toISOString(),
      last_updated_at: log.last_updated_at,
      metadata: log.metadata
    })) as EmailLog[];
    
    return emailLogs;
  };

  const resendEmail = async (emailLog: EmailLog): Promise<void> => {
    try {
      console.log("Attempting to resend email:", emailLog);
      
      const { data, error } = await supabase
        .functions
        .invoke("send-lead-intro", {
          body: {
            introId: emailLog.entity_id,
            toEmail: emailLog.to_email,
            toName: emailLog.to_name,
            fromName: emailLog.from_name,
            subject: emailLog.subject
          }
        });

      if (error) {
        throw new Error(`Failed to resend email: ${error.message || "Unknown error"}`);
      }
      
      toast.success("Email resent successfully");
      return data;
    } catch (err: any) {
      console.error("Error resending email:", err);
      toast.error(err.message || "Failed to resend email");
      throw err;
    }
  };
  
  const queryClient = useQueryClient();
  
  const resendEmailMutation = useMutation({
    mutationFn: resendEmail,
    onSuccess: () => {
      // Invalidate the email logs query to refresh the data
      queryClient.invalidateQueries({ queryKey: ["emailLogs"] });
    }
  });
  
  return {
    ...useQuery({
      queryKey: ["emailLogs", filter],
      queryFn: fetchEmailLogs,
    }),
    resendEmail: resendEmailMutation.mutate,
    isResending: resendEmailMutation.isPending
  };
};
